import { useState, useEffect, useRef } from 'react'
import { apiClient, useAuthStore } from '@indipia/shared'
import { fetchScheduledSiteIds } from '../api/scheduleApi'
import { SiteRegistrationPopup, useDefaultMemberFilter, useMineOnlyState } from '@indipia/shared-features'
import {
  Alert, Button, Checkbox, EmptyState, ErrorState, Page, PageHeader, PageScroll, Search, Skeleton, Steps, Tooltip,
} from '@indipia/ui-core'
import type { NavFn } from '../pages/SchedulerPage'
import { InfoBanner } from '../components/InfoBanner'
import { useCommonCodes } from '../context/CommonCodesContext'
import { buildSampleRaw } from './rawMatch'
import type { RawExtract } from './ExtractRawView2'

type Props = {
  navigate: NavFn
  /** 선택한 현장 id를 부모(SchedulerPage)에 알려 다음 단계(견적서 업로드/공종·기간 선택)까지
   *  들고 갈 수 있게 한다. 선택 없이 진행하면 null. */
  onSiteSelected: (siteId: number | null) => void
  /** 스튜디오 홈 진입모드(entry=QUOTE/MANUAL) — 스텝 표시가 모든 경로 3단계라 이 뷰에서는 쓰지 않음(부모 호환용 props). */
  entry?: string | null
  /** [테스트 전용] 업로드·분석 없이 샘플 추출 결과로 '추출 결과 확인' 화면에 진입한다(운영 빌드에서는 버튼을 그리지 않음). */
  onDevSample?: (siteId: number, raw: RawExtract) => void
}

interface SiteSummary {
  siteId:       number
  siteName:     string
  customerName: string
  customerMobile: string
  siteStatus:   string
}

const INITIAL_LIMIT = 4
// 펼친 뒤 스크롤이 바닥에 닿을 때마다 추가로 그리는 개수
const CHUNK = 20

// 기획 3단계(현장 선택 / 생성 방법 선택 / 공종·공사일 선택) — 직접 생성(MANUAL)도 3단계로 표시한다(F1 이동 규칙 §5).
// 초기화(재생성) 흐름만 현장 선택이 없어 resetMode(2단계).
// quote: 견적서 자동 생성(QUOTE) 화면(업로드·공종표)에서는 앞 단계 이름을 '견적서 업로드'로 보여 준다 — 생성 방법을 고르는 화면(create-upload)은 그대로 '생성 방법 선택'.
function StepIndicator({ step, resetMode, quote }: { step: number; resetMode?: boolean; quote?: boolean }) {
  const method = quote ? '견적서 업로드' : '생성 방법 선택'
  const steps = resetMode
    ? [method, '공종/공사일 선택']
    : ['현장 선택', method, '공종/공사일 선택']
  // 정렬·상하 여백은 컴포넌트의 .steps-wrap 이 담당한다(D-09 §15.22)
  return <Steps steps={steps} current={step} />
}

export { StepIndicator }

export function SiteSelectView({ navigate, onSiteSelected, onDevSample }: Props) {
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  const [sites,      setSites]      = useState<SiteSummary[]>([])
  const [loading,    setLoading]    = useState(false)
  const [apiError,   setApiError]   = useState(false)
  const [expanded,   setExpanded]   = useState(false)
  const [search,     setSearch]     = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [visibleCount, setVisibleCount] = useState(INITIAL_LIMIT)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const [sitePopupOpen, setSitePopupOpen] = useState(false)

  // '☑ 내 현장만'(2026-10-12 개선사항 B-0 v1.9) — 내가 현장 담당자 또는 관리자인 현장만.
  // 직원은 기본 체크, 사장님은 기본 해제. 서버(C-1/C-2 memberIds)가 거르므로 '더 보기' 건수도 이 조건을 반영한다.
  const { myId } = useDefaultMemberFilter()
  // 로그인 정보가 늦게 들어와도 직원은 체크, 사장님은 해제로 맞춘다
  const [mineOnly, setMineOnly] = useMineOnlyState()
  const mineIds = mineOnly && myId != null ? [myId] : []
  // 선택한 현장 — '내 현장만'을 다시 켜도 목록에서 숨기지 않는다(목록에 있으면 원래 자리, 빠졌으면 맨 위)
  const [pinned, setPinned] = useState<SiteSummary | null>(null)
  // 새로 등록한 현장 — 목록 맨 위에 둔다
  const [newSiteId, setNewSiteId] = useState<number | null>(null)
  // 신규 등록 팝업에서 고른 현장이 이미 스케줄을 갖고 있으면 그 현장 id (저장 단계에서 교체 여부를 묻는다)
  const [scheduledPickId, setScheduledPickId] = useState<number | null>(null)
  // 마지막으로 받은 스케줄 있는 현장 id 모음
  const excludedRef = useRef<Set<number>>(new Set())

  // 이미 스케줄이 있는 현장은 서버가 제외하고 내려준다(excludeSiteIds) — 클라에서 전량 받아
  // 차집합하던 방식 제거. 제외 대상이 많아(>200) CSV 쿼리 한계를 넘으면 POST /sites/search로 분기.
  const loadSites = async (compId: number, memberIds: number[] = mineIds): Promise<SiteSummary[]> => {
    setLoading(true)
    setApiError(false)
    const headers = { 'X-Comp-Id': String(compId) }
    try {
      const excludeSiteIds = await fetchScheduledSiteIds(compId)
      excludedRef.current = new Set(excludeSiteIds)

      let items: SiteSummary[]
      if (excludeSiteIds.length <= 200) {
        const siteRes = await apiClient.get<{ data: { items: SiteSummary[] } }>('/customer/sites', {
          params: {
            site_status_not: 'DONE',
            size: 200,
            ...(excludeSiteIds.length > 0 ? { excludeSiteIds: excludeSiteIds.join(',') } : {}),
            ...(memberIds.length > 0 ? { memberIds: memberIds.join(',') } : {}),
          },
          headers,
        })
        items = siteRes.data.data?.items ?? []
      } else {
        const siteRes = await apiClient.post<{ data: { items: SiteSummary[] } }>('/customer/sites/search', {
          excludeSiteIds,
          siteStatusNot: 'DONE',
          ...(memberIds.length > 0 ? { memberIds } : {}),
        }, { headers })
        items = siteRes.data.data?.items ?? []
      }
      setSites(items)
      return items
    } catch {
      setSites([])
      setApiError(true)
      return []
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!user?.compId) return
    loadSites(user.compId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.compId, mineOnly, myId])

  const selectSite = (s: SiteSummary) => {
    const next = selectedId === s.siteId ? null : s.siteId
    setSelectedId(next)
    setPinned(next == null ? null : s)
  }

  const handleNext = () => {
    onSiteSelected(selectedId)
    navigate('create-upload')
  }

  // 새로 등록한 현장은 맨 위, 선택한 현장은 조건에서 빠졌을 때만 맨 위에 붙여 선택을 유지한다
  const listed = (() => {
    let list = sites
    if (pinned && !list.some(s => s.siteId === pinned.siteId)) list = [pinned, ...list]
    if (newSiteId != null) {
      const created = list.find(s => s.siteId === newSiteId)
      if (created) list = [created, ...list.filter(s => s.siteId !== newSiteId)]
    }
    return list
  })()
  const filtered = search.trim()
    ? listed.filter(s => {
        const q = search.toLowerCase()
        return s.siteName.toLowerCase().includes(q) || s.customerName.toLowerCase().includes(q)
      })
    : listed

  // 펼치기 전에는 INITIAL_LIMIT 만, 펼친 뒤에는 스크롤이 바닥에 닿을 때마다 CHUNK 씩 더 그린다.
  // (목록은 이미 전부 받아둔 상태라 추가 요청은 없고, 렌더 개수만 늘린다)
  const visible  = filtered.slice(0, visibleCount)
  const hasMore  = filtered.length > visibleCount

  // 검색어가 바뀌면 렌더 개수를 처음으로 되돌린다
  useEffect(() => {
    setVisibleCount(expanded ? CHUNK : INITIAL_LIMIT)
  }, [search, expanded])

  // 무한 스크롤 — 목록 끝의 sentinel 이 화면에 들어오면 다음 묶음을 그린다.
  // (IntersectionObserver 대신 스크롤 위치로 판정 — 어떤 조상이 스크롤 컨테이너든 동일하게 동작한다)
  useEffect(() => {
    if (!expanded || !hasMore) return
    const check = () => {
      const el = sentinelRef.current
      if (!el) return
      if (el.getBoundingClientRect().top < window.innerHeight + 160) {
        setVisibleCount(c => Math.min(c + CHUNK, filtered.length))
      }
    }
    check() // 펼친 직후 화면이 덜 찼으면 바로 이어 그린다
    window.addEventListener('scroll', check, true)
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('scroll', check, true)
      window.removeEventListener('resize', check)
    }
  }, [expanded, hasMore, filtered.length])

  const openSitePopup = () => setSitePopupOpen(true)

  return (
    // D-07 §21.5 L6(스텝 위저드) — Steps·액션 푸터를 고정하고 가운데를 스크롤시킨다.
    // 스크롤을 가진 쪽(PageScroll)이 폭과 패딩을 갖는다(§21.4).
    <Page layout="fixed">
      <div className="shrink-0">
        <StepIndicator step={0} />
      </div>

      <PageScroll width="md">
        <PageHeader
          title="현장을 선택해주세요"
          extra={
            <Button variant="primary" size="md" className="gap-1.5" onClick={openSitePopup}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              신규 현장 등록
            </Button>
          }
        />

        <InfoBanner>
          현장을 검색해 선택하거나 새로 등록한 뒤, <b>[다음]</b>을 눌러 공사스케줄을 만들어보세요
        </InfoBanner>

        {/* 신규 등록 팝업에서 고른 현장에 이미 스케줄이 있는 경우 — 막지 않고 안내만 */}
        {scheduledPickId != null && selectedId === scheduledPickId && (
          <Alert kind="info" className="mt-07">
            이미 스케줄이 있어요. 저장할 때 교체 여부를 물어봐요
          </Alert>
        )}

        {/* 목록 섹션 — 제목·검색·상태블록·카드 그리드·더보기를 한 <section> 이 소유한다.
            상태블록(.state-block)은 그리드 밖에 두어 전체 폭을 그대로 쓴다(col-span-full 래퍼 불필요).
            mt-09(48) 은 배너와의 간격 — .page-scroll 이 block flow 라 InfoBanner 의 하단 마진(16)과
            병합되어 48 이 그대로 남는다. 배너를 닫아도(null 반환) 간격이 유지된다. */}
        <section className="mt-09">
          <div className="mb-03 flex items-center gap-03">
            <span className="section-title">시공중인 현장 (스케줄 없음)</span>
            {/* ☑ 내 현장만 — 제목 줄 오른쪽(B-0 v1.9) */}
            <Tooltip tip="내가 현장 담당자 또는 현장 관리자인 현장만 보기" className="ml-auto">
              <Checkbox label="내 현장만" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            </Tooltip>
          </div>

          {expanded && (
            <div className="mb-2.5">
              <Search
                autoFocus
                value={search}
                onChange={setSearch}
                placeholder="현장명 / 고객명 검색"
              />
            </div>
          )}

          {/* 로딩 → 실패 ErrorState / 0건 EmptyState / 데이터 Content (D-07 §17.1).
              스켈레톤은 카드 자리를 그대로 모사해 로딩이 풀릴 때 레이아웃이 튀지 않게 한다. */}
          {apiError ? (
            <ErrorState
              className="mb-3.5"
              description="현장 정보를 불러올 수 없어요. 고객 서비스 연결을 확인해주세요."
              onRetry={() => { if (user?.compId) loadSites(user.compId) }}
            />
          ) : !loading && visible.length === 0 ? (
            <EmptyState
              className="mb-3.5"
              title={search ? '검색 결과가 없어요' : mineOnly ? '내가 담당·관리하는 현장이 없어요' : '스케줄 없는 현장이 없어요'}
              description={search ? '다른 검색어로 찾아보세요' : mineOnly ? "'내 현장만'을 해제하면 전체 현장을 볼 수 있어요" : '새 현장을 등록해 공사스케줄을 만들어보세요'}
              action={search ? undefined : (
                <Button variant="secondary" onClick={openSitePopup}>＋ 신규 현장 등록</Button>
              )}
            />
          ) : (
            <div className="mb-3.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {loading ? (
                [0, 1, 2, 3].map(i => <Skeleton key={i} height={52} />)
              ) : visible.map(s => {
                const isSel = selectedId === s.siteId
                return (
                  <div
                    key={s.siteId}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectSite(s)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectSite(s) } }}
                    className="flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 transition-colors"
                    style={{
                      background:  isSel ? 'var(--gt-subtle)' : 'var(--shell-surface)',
                      borderColor: isSel ? 'var(--gt-primary)' : 'var(--shell-border)',
                      boxShadow:   isSel ? 'var(--elevation-2)' : 'none',
                    }}
                  >
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-group"
                      style={{ background: 'var(--gt-subtle)' }}
                    >
                      {isSel ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      )}
                    </div>
                    {/* 현장명 · 고객명 · 연락처를 고정 폭 칼럼으로 — 카드마다 같은 위치에서 시작해
                        목록 전체가 세로로 정렬돼 보인다. 넘치는 값은 각 칼럼 안에서만 줄인다. */}
                    <div
                      className="grid min-w-0 flex-1 items-center gap-x-2"
                      style={{ gridTemplateColumns: 'minmax(0,1fr) 60px 82px' }}
                    >
                      <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-body-sm font-semibold text-text">
                        {s.siteName || '—'}
                      </span>
                      <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-caption text-text-muted">
                        {s.customerName || '—'}
                      </span>
                      <span className="whitespace-nowrap text-caption tabular-nums text-text-disabled">
                        {s.customerMobile || ''}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* 펼치기 전에만 버튼. 펼친 뒤에는 아래 sentinel 이 스크롤에 맞춰 알아서 이어 붙인다 */}
          {!loading && !expanded && hasMore && (
            <Button
              variant="etc"
              className="mb-3.5 w-full gap-1.5"
              onClick={() => { setExpanded(true); setVisibleCount(CHUNK) }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              더 보기 ({filtered.length - INITIAL_LIMIT}개 더 있음)
            </Button>
          )}

          {/* 무한 스크롤 감지점 — 다음 묶음이 그려질 자리를 스켈레톤으로 잡는다(D-07 §3.2 #7) */}
          {!loading && expanded && hasMore && (
            <div ref={sentinelRef} className="mb-3.5">
              <Skeleton height={52} />
            </div>
          )}
          {!loading && expanded && !hasMore && filtered.length > 0 && (
            <div className="mb-3.5 py-1 text-center text-caption text-text-disabled">
              전체 {filtered.length}개 현장을 모두 불러왔습니다
            </div>
          )}
        </section>
      </PageScroll>

      {/* 액션 푸터 — Page(fixed)의 고정 자식이라 목록이 길어져도 화면 아래에 남는다(D-07 §21.5 L6) */}
      <div className="shrink-0 border-t border-border bg-surface px-8 py-4">
        <div className="lay-w-md">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleNext}
            disabled={selectedId == null}
          >
            다음
          </Button>
          {/* 테스트 전용 — 운영 빌드(MODE=production)에서는 그리지 않는다 */}
          {onDevSample && import.meta.env.MODE !== 'production' && (
            <Button
              variant="etc"
              className="mt-2 w-full"
              onClick={() => selectedId != null && onDevSample(selectedId, buildSampleRaw(codes?.workTypes ?? []))}
              disabled={selectedId == null || !codes}
            >
              [테스트] 추출 결과 확인 화면으로 바로 가기
            </Button>
          )}
        </div>
      </div>

      {sitePopupOpen && (
        <SiteRegistrationPopup
          agentId={8}
          onClose={() => setSitePopupOpen(false)}
          onComplete={async (siteId: number) => {
            setSitePopupOpen(false)
            setSelectedId(siteId)
            setScheduledPickId(null)
            if (!user?.compId) return
            // 새 현장은 목록 맨 위에 두고 선택한다(등록자 본인이 기본 현장 담당자라 '내 현장만'에서도 보인다)
            const items = await loadSites(user.compId)
            setNewSiteId(siteId)
            let picked: SiteSummary | null = items.find(s => s.siteId === siteId) ?? null
            if (!picked) {
              // 목록에 없다 = 이미 스케줄이 있는 현장 등 — 막지 않고 카드로 보여 주며 안내한다
              if (excludedRef.current.has(siteId)) setScheduledPickId(siteId)
              try {
                const res = await apiClient.get<{ data: SiteSummary[] }>('/customer/sites/batch', {
                  params: { siteIds: String(siteId) },
                  headers: { 'X-Comp-Id': String(user.compId) },
                })
                picked = (res.data.data ?? []).find(d => d.siteId === siteId) ?? null
              } catch { /* 카드만 못 그릴 뿐 선택은 유지 */ }
            }
            setPinned(picked)
          }}
        />
      )}
    </Page>
  )
}
